/**
 * The reading surface's tokenizer — ONE implementation, shared by both sides (M4a step 2).
 *
 * The client draws `.nn-w` spans from `tokenize(line.text)` and reports `charStart/charEnd` on
 * every event; the resolver writes `line_item.char_start/char_end` from the same function so a
 * mark, a tap target and a stored item span are always the same characters. That is why this
 * module lives here (zero imports, like the protected `subtitles.ts`) and is aliased into the web
 * bundle as `@nelnela/tokenize` — `packages/web/src/lib/text.ts` re-exports it rather than
 * keeping a copy. `test/tokenize.test.ts` pins the offsets on both sides.
 *
 * Offsets are JavaScript string indices (UTF-16 code units). Georgian Mkhedruli is entirely in
 * the BMP, so for the Phase-1 pair they are also code points; anything outside the BMP (an emoji
 * in a caption) still round-trips because both sides slice with the same units.
 *
 * Ported verbatim from the local app's Transcript.jsx by M2 (reading-surface-spec §1, §3).
 */

/** Strip leading/trailing non-letter/non-digit runs — the tappable "word" inside a token. */
export const WORD_TRIM = /^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu;

export interface Token {
  /** the raw whitespace-delimited chunk as it appears in the line (rendered verbatim) */
  raw: string;
  /** the trimmed word sent to gloss/exposure; '' when the token is punctuation only */
  word: string;
  /** char offsets of `raw` in the line text — carried on events as context.charStart/charEnd */
  charStart: number;
  charEnd: number;
}

/**
 * Split a line into tokens + whitespace runs, keeping offsets. Whitespace comes back as tokens
 * with word='' and raw=the whitespace so the caller can render it as a bare text node (spec §3:
 * "whitespace as bare text nodes"); callers distinguish them with `isWhitespace`.
 */
export function tokenize(text: string): Token[] {
  const out: Token[] = [];
  const re = /\s+|\S+/gu;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text)) !== null) {
    const raw = m[0];
    const ws = /^\s+$/u.test(raw);
    out.push({
      raw,
      word: ws ? '' : raw.replace(WORD_TRIM, ''),
      charStart: m.index,
      charEnd: m.index + raw.length,
    });
  }
  return out;
}

export const isWhitespace = (t: Token): boolean => /^\s+$/u.test(t.raw);

/**
 * The word tokens of a line with the offsets of the WORD (not the raw token): punctuation is
 * trimmed off both ends, so `[charStart, charEnd)` selects exactly what the resolver stores and
 * what a gloss request sends. Punctuation-only tokens are dropped.
 *
 * `index` is the index into `tokenize(text)` — the client's `data-tok`, and the coordinate the
 * matcher's slots work in.
 */
export interface WordToken extends Token {
  index: number;
}
export function wordTokens(text: string): WordToken[] {
  const out: WordToken[] = [];
  const all = tokenize(text);
  for (let i = 0; i < all.length; i++) {
    const t = all[i]!;
    if (!t.word) continue;
    // The word sits somewhere inside raw; indexOf is exact because WORD_TRIM only removes runs
    // from the two ends (the word is a contiguous slice of raw).
    const inner = t.raw.indexOf(t.word);
    const charStart = t.charStart + (inner === -1 ? 0 : inner);
    out.push({
      raw: t.raw,
      word: t.word,
      charStart,
      charEnd: charStart + t.word.length,
      index: i,
    });
  }
  return out;
}

/**
 * The lookup key for a surface form: what goes into `lemma_cache.surface` and what the LLM
 * lemmatizer is asked about. Georgian is unicase, but transcripts carry Latin names and digits —
 * lowercasing keeps "Rustavi" and "rustavi" one cache row (reading-surface-spec §1: "lowercase
 * before lookups"), and NFC keeps a decomposed paste from missing the same row.
 */
export function surfaceKey(word: string): string {
  return word.normalize('NFC').toLowerCase();
}
